import {Component, inject, Input, OnChanges, OnInit} from '@angular/core';
import {ContentComponent} from "../content/content.component";
import {HeadlineComponent} from "../headline/headline.component";
import {NgOptimizedImage} from "@angular/common";
import {TimePastPipe} from "../../pipes/time-past.pipe";
import {TranslatePipe} from "../../pipes/translate.pipe";
import {ProfileService} from "../../services/profile.service";

@Component({
    selector: 'app-avatar',
    imports: [
        ContentComponent,
        HeadlineComponent,
        NgOptimizedImage,
        TimePastPipe,
        TranslatePipe
    ],
    templateUrl: './avatar.component.html',
    styleUrl: './avatar.component.scss'
})
export class AvatarComponent implements OnInit, OnChanges {
  private profileService: ProfileService = inject(ProfileService);
  @Input() public image: string = '';
  @Input() public name: string = '';
  @Input() public subTitle: string = '';
  @Input() public date: Date | null = null;
  @Input() public editDate: Date | null = null;
  @Input() public type: 'xxs' | 'small' | 'medium' | 'large' | 'xxl' | 'profile' | 'default' = 'default';
  @Input() public icon: 'person' | 'group' | 'translate' | 'mail' = 'person';
  @Input() public border: boolean = true;
  @Input() public padding: boolean = true;
  @Input() public inactive: boolean = false;
  @Input() public profileUserId: number | null = null;
  @Input() public openProfileOnClick: boolean = false;

  public disableImageOptimization: boolean = false;
  public height: number = 60;
  public width: number = 60;
  public containerClasses: string = '';
  public defaultIconClasses: string = '';

  public ngOnInit(): void {
    this.setImageOptimization();
    this.setDimensions();
    this.setClasses();
  }

  public ngOnChanges(): void {
    this.setImageOptimization();
    this.setDimensions();
    this.setClasses();
  }

  public onImageError(): void {
    this.image = '';
  }

  public canOpenProfile(): boolean {
    const currentUserId = this.profileService.profile()?.user?.id;
    return this.openProfileOnClick && this.profileUserId !== null && this.profileUserId !== currentUserId;
  }

  public onAvatarClick(event: MouseEvent): void {
    if (!this.canOpenProfile() || this.profileUserId === null) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.profileService.showProfile(this.profileUserId);
  }

  private setImageOptimization(): void {
    // Disable optimization for GIFs and base64 data URLs
    this.disableImageOptimization = this.image?.includes('.gif') || this.image?.startsWith('data:');
  }

  private setDimensions(): void {
    switch (this.type) {
      case 'xxs':
        this.height = 24;
        this.width = 24;
        break;
      case 'small':
        this.height = 24;
        this.width = 24;
        break;
      case 'medium':
        this.height = 40;
        this.width = 40;
        break;
      case 'large':
        this.height = 60;
        this.width = 60;
        break;
      case 'xxl':
        this.height = 60;
        this.width = 60;
        break;
      case 'profile':
        this.height = 220;
        this.width = 220;
        break;
      default:
        this.height = 60;
        this.width = 60;
    }
  }

  private setClasses(): void {
    switch (this.type) {
      case 'xxs':
        this.containerClasses = 'e-avatar e-avatar-circle e-avatar-xsmall flex-shrink-0';
        this.defaultIconClasses = 'mts-icon p-m';
        break;
      case 'small':
        this.containerClasses = 'e-avatar e-avatar-circle e-avatar-small flex-shrink-0';
        this.defaultIconClasses = 'mts-icon p-m';
        break;
      case 'medium':
        this.containerClasses = 'e-avatar e-avatar-circle flex-shrink-0';
        this.defaultIconClasses = 'mts-icon p-m fs-4';
        break;
      case 'large':
        this.containerClasses = 'e-avatar e-avatar-circle e-avatar-large flex-shrink-0';
        this.defaultIconClasses = 'mts-icon p-m fs-4';
        break;
      case 'xxl':
        this.containerClasses = 'e-avatar e-avatar-circle e-avatar-xxlarge flex-shrink-0';
        this.defaultIconClasses = 'mts-icon p-m fs-4';
        break;
      case 'profile':
        this.containerClasses = 'profile';
        this.defaultIconClasses = 'mts-icon fs-1';
        break;
      default:
        this.containerClasses = 'e-avatar e-avatar-circle e-avatar-large flex-shrink-0 m-1';
        this.defaultIconClasses = 'mts-icon p-m fs-4';
        break;
    }
  }
}
